import { useEffect, useState } from "react";

/** Период опроса `/version.json`. */
export const VERSION_CHECK_INTERVAL_MS = 60_000;
/** Не чаще одной проверки за это время (вкладка «вернулась» + сеть «появилась»
 * разом, частые переключения вкладок). */
export const VERSION_CHECK_MIN_GAP_MS = 10_000;

/** Сколько ждать ответ `/version.json`: зависший запрос иначе навсегда держал бы `inFlight`. */
export const VERSION_FETCH_TIMEOUT_MS = 10_000;

/** Версия сборки, которая сейчас лежит на сервере, или `null`, если проверка
 * не удалась (причина — в `console.warn`). Сбой проверки — НЕ «новая версия»:
 * nginx на несуществующий файл отдал бы index.html со статусом 200, и плашка
 * от каждого обрыва Wi-Fi на складе моргала бы зря. */
export async function fetchServerVersion(): Promise<string | null> {
  // AbortController + setTimeout, не AbortSignal.timeout: старые webview на ТСД его не знают.
  // Таймер живёт и на чтении тела — зависший разбор тоже не должен держать inFlight.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), VERSION_FETCH_TIMEOUT_MS);
  try {
    return await readServerVersion(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

async function readServerVersion(signal: AbortSignal): Promise<string | null> {
  let res: Response;
  try {
    res = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store", signal });
  } catch (err) {
    console.warn("Проверка версии: запрос не прошёл", err);
    return null;
  }
  if (!res.ok) {
    console.warn(`Проверка версии: HTTP ${res.status}`);
    return null;
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    console.warn(`Проверка версии: ответ не JSON (content-type «${contentType}»)`);
    return null;
  }
  let body: unknown;
  try {
    body = await res.json();
  } catch (err) {
    console.warn("Проверка версии: JSON не разбирается", err);
    return null;
  }
  const version =
    body && typeof body === "object" ? (body as { version?: unknown }).version : undefined;
  if (typeof version !== "string" || version.trim() === "") {
    console.warn("Проверка версии: в ответе нет поля version", body);
    return null;
  }
  return version;
}

/** `true`, когда на сервере ПОДТВЕРЖДЁННО другая сборка фронта, чем загружена
 * во вкладке. Опрос — раз в минуту, при возврате на вкладку и при появлении
 * сети (не чаще раза в 10 с). Обнаружив расхождение, перестаёт опрашивать:
 * флаг держится до перезагрузки страницы, саму страницу не перезагружает
 * (решение владельца 23.10.2026 — автоперезагрузка сбросила бы скан). */
export function useNewVersion(currentVersion: string = __BUILD_ID__): boolean {
  const [outdated, setOutdated] = useState(false);

  useEffect(() => {
    let stopped = false;
    let inFlight = false;
    let lastCheckAt = -Infinity;

    const stop = (): void => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", check);
    };

    async function check(): Promise<void> {
      if (stopped || inFlight) return;
      // performance.now(), не Date.now(): стенные часы ТСД при NTP-синхронизации
      // прыгают назад, и троттлинг молчал бы до «догона».
      const now = performance.now();
      if (now - lastCheckAt < VERSION_CHECK_MIN_GAP_MS) return;
      lastCheckAt = now;
      inFlight = true;
      try {
        const server = await fetchServerVersion();
        if (stopped || server === null) return;
        if (server !== currentVersion) {
          stop();
          setOutdated(true);
        }
      } finally {
        inFlight = false;
      }
    }

    function onVisibility(): void {
      if (document.visibilityState === "visible") void check();
    }

    const timer = window.setInterval(() => void check(), VERSION_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", check);
    void check();
    return stop;
  }, [currentVersion]);

  return outdated;
}
